import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import DOMPurify from 'dompurify';
import {
  SchwachstelleVersionList,
  SchwachstelleVersion,
  AuditVersionList,
  AuditVersion,
} from '../../types';
import { historieService } from '../../services/historieService';

type VersionType = 'schwachstelle' | 'audit';

interface VersionsHistorieProps {
  type: VersionType;
  entityId: number;
  open: boolean;
  onClose: () => void;
  onRestore?: () => void; // Callback after restore to refresh parent
}

interface FieldDef {
  key: string;
  label: string;
}

const SCHWACHSTELLE_FIELDS: FieldDef[] = [
  { key: 'detailbeschreibung', label: 'Detailbeschreibung' },
  { key: 'allgemeine_beschreibung', label: 'Allgemeine Beschreibung' },
  { key: 'mitigationsmassnahmen', label: 'Mitigationsmaßnahmen' },
];

const AUDIT_FIELDS: FieldDef[] = [
  { key: 'management_summary', label: 'Management Summary' },
  { key: 'vorbemerkungen', label: 'Vorbemerkungen' },
];

function formatDateTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function stripHtml(html: string | undefined | null): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

const HtmlPreview: React.FC<{ html: string | undefined | null; label: string }> = ({ html, label }) => {
  const theme = useTheme();

  if (!html) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{label}</Typography>
        <Typography variant="body2" color="text.disabled" fontStyle="italic">Kein Inhalt</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>{label}</Typography>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          '& p': { margin: '0.25em 0' },
          '& ul, & ol': { pl: 2.5, my: 0.5 },
          maxHeight: 300,
          overflowY: 'auto',
        }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
    </Box>
  );
};

const DiffView: React.FC<{
  oldVersion: Record<string, any> | null;
  newVersion: Record<string, any>;
  fields: FieldDef[];
}> = ({ oldVersion, newVersion, fields }) => {
  const theme = useTheme();

  return (
    <Box>
      {fields.map((field) => {
        const oldVal = oldVersion ? stripHtml(oldVersion[field.key]) : '';
        const newVal = stripHtml(newVersion[field.key]);
        const changed = oldVal !== newVal;

        return (
          <Box key={field.key} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{field.label}</Typography>
              {changed && <Chip label="Geändert" size="small" color="warning" variant="outlined" />}
              {!changed && oldVersion && <Chip label="Unverändert" size="small" variant="outlined" />}
            </Box>
            <HtmlPreview html={newVersion[field.key]} label="" />
          </Box>
        );
      })}
    </Box>
  );
};

const VersionsHistorie: React.FC<VersionsHistorieProps> = ({
  type,
  entityId,
  open,
  onClose,
  onRestore,
}) => {
  const theme = useTheme();
  const [versions, setVersions] = useState<(SchwachstelleVersionList | AuditVersionList)[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<(SchwachstelleVersion | AuditVersion) | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const fields = type === 'schwachstelle' ? SCHWACHSTELLE_FIELDS : AUDIT_FIELDS;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setSelectedVersion(null);

    const loadVersions = type === 'schwachstelle'
      ? historieService.getSchwachstelleVersionen(entityId)
      : historieService.getAuditVersionen(entityId);

    loadVersions
      .then((data) => setVersions(data))
      .catch(() => setError('Versionen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, type, entityId]);

  const handleSelectVersion = async (versionNummer: number) => {
    setLoadingDetail(true);
    try {
      const data = type === 'schwachstelle'
        ? await historieService.getSchwachstelleVersion(entityId, versionNummer)
        : await historieService.getAuditVersion(entityId, versionNummer);
      setSelectedVersion(data);
    } catch {
      setError('Version konnte nicht geladen werden');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedVersion) return;
    setRestoring(true);
    try {
      if (type === 'schwachstelle') {
        await historieService.restoreSchwachstelleVersion(entityId, selectedVersion.version_nummer);
      } else {
        await historieService.restoreAuditVersion(entityId, selectedVersion.version_nummer);
      }
      setRestoreDialogOpen(false);
      onRestore?.();
      onClose();
    } catch {
      setError('Wiederherstellung fehlgeschlagen');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640, md: 760 }, p: 0, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Versionshistorie
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Version list (left panel) */}
          <Box sx={{ width: 240, borderRight: 1, borderColor: 'divider', overflowY: 'auto', flexShrink: 0 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && versions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                Keine Versionen vorhanden.
              </Typography>
            )}
            <List disablePadding>
              {versions.map((v) => (
                <ListItemButton
                  key={v.version_nummer}
                  selected={selectedVersion?.version_nummer === v.version_nummer}
                  onClick={() => handleSelectVersion(v.version_nummer)}
                  sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={700}>V{v.version_nummer}</Typography>
                        {v.kommentar && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                            {v.kommentar}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {v.erstellt_von_name || `User #${v.erstellt_von_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {formatDateTime(v.erstellt_am)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Version detail (right panel) */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loadingDetail && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loadingDetail && !selectedVersion && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                <CompareArrowsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography>Wähle eine Version aus der Liste</Typography>
              </Box>
            )}

            {!loadingDetail && selectedVersion && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Version {selectedVersion.version_nummer}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(selectedVersion as any).erstellt_von_name || `User #${selectedVersion.erstellt_von_id}`}
                      {' – '}
                      {formatDateTime(selectedVersion.erstellt_am)}
                    </Typography>
                    {selectedVersion.kommentar && (
                      <Chip label={selectedVersion.kommentar} size="small" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={() => setRestoreDialogOpen(true)}
                    size="small"
                  >
                    Wiederherstellen
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <DiffView oldVersion={null} newVersion={selectedVersion as Record<string, any>} fields={fields} />
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Version wiederherstellen?</DialogTitle>
        <DialogContent>
          <Typography>
            Die aktuellen Textfelder werden auf den Stand von Version {selectedVersion?.version_nummer} zurückgesetzt.
            Es wird dabei eine neue Version angelegt, sodass die Änderung nachvollziehbar bleibt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>Abbrechen</Button>
          <Button onClick={handleRestore} variant="contained" color="warning" disabled={restoring}>
            {restoring ? <CircularProgress size={20} /> : 'Wiederherstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VersionsHistorie;
